/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */

import { Button, Center, Loader, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconAdjustmentsAlt, IconDownload, IconUpload } from '@tabler/icons-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EORIGIN_DATE_FORMAT_WITH_TIME, Transit, TransitGroup, TransitsCriteria } from '@e-origin/shared';

import { getInitialTransitCriteria, prefetchAdminTransits, useAdminExportTransit, useAdminTransits, useTransitCriteriaUrlSync } from '@admin/hooks';
import { SearchBox, Table, TableActions, TableColumnProps } from '@components';
import { BATCH_LIST_NEXT_PAGE } from '@constants';
import { ContentLayout } from '@features/layout';
import { TransitType } from '@features/transits';
import { extractStatus } from '@features/transits/utils';
import { removeUndefined } from '@utils';
import { ChangeTransitStatus } from './ChangeTransitStatus';
import { ImportTransit } from './ImportTransit';

export const ManageTransits = () => {
  const overflowEllipsisStyle = { maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', textWrapMode: 'nowrap' };

  const theme = useMantineTheme();
  const exportTransitMutation = useAdminExportTransit();
  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialTransitCriteria(searchParams);

  const [filters, setFilters] = useState<TransitsCriteria['filters']>(initialCriteria.filters);
  const [sorting, setSorting] = useState<TransitsCriteria['sorting']>(initialCriteria.sorting);
  const [pagination, setPagination] = useState<TransitsCriteria['pagination']>(initialCriteria.pagination);
  useTransitCriteriaUrlSync({ filters, sorting, pagination });

  const [transitsData, setTransitsData] = useState<Transit[]>([]);
  const [transitView, setTransitView] = useState<TransitGroup | undefined>(initialCriteria.filters.group ?? TransitGroup.DEPARTURE);

  const { data: { list: transitsList, total: totalTransitCount } = { list: [], total: 0 }, isLoading } = useAdminTransits({ filters, sorting, pagination });

  const setTransitsFilters = (updatedFilters: Partial<TransitsCriteria['filters']>) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...updatedFilters,
    });
    setFilters(mergedFilters);
  };

  useEffect(() => {
    if (!transitsList.length && !transitsData.length) {
      return;
    }

    setTransitsData(transitsList);
  }, [transitsList]);

  const prefetchNextPage = (page: number) => prefetchAdminTransits({ filters, sorting, pagination: { ...pagination, page } });

  const loadTransits = (transitViewType?: TransitGroup) => {
    const tableView = transitViewType ?? transitView;

    setTransitsFilters({
      group: tableView,
    });
  };

  const searchTransits = (searchQuery: string) => {
    setTransitsFilters({ searchQuery });
    setPagination({ page: 1, size: pagination.size });
    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
    prefetchNextPage(page + 1);
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  const openChangeTransitStatusModal = (transit: Transit) => {
    modals.open({
      title: 'Change Transit Status',
      children: <ChangeTransitStatus onClose={() => modals.closeAll()} transit={transit} />,
      size: 'lg',
      centered: true,
    });
  };

  const openImportTransitModal = () => {
    let modalTitle = 'Import Transit';

    if (transitView) {
      switch (transitView) {
        case TransitGroup.DEPARTURE:
          modalTitle = 'Import Departure Transit';
          break;
        case TransitGroup.ARRIVAL:
          modalTitle = 'Import Arrival Transit';
          break;
        default:
          modalTitle = 'Import Transit';
      }
    }

    modals.open({
      title: modalTitle,
      children: <ImportTransit onClose={() => modals.closeAll()} transitView={transitView} />,
      size: 'lg',
      centered: true,
    });
  };

  const columns: TableColumnProps<Transit>[] = [
    {
      name: 'Transit ID',
      selector: 'counter',
      width: '120px',
      cell: (row) => row.counter,
      sortable: true,
    },
    {
      name: 'Transit name',
      selector: 'name',
      sortable: true,
      cell: (row) => row.name,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Transit status',
      selector: 'generalInfo.status',
      sortable: true,
      cell: (row) => extractStatus(row.generalInfo.status),
      style: overflowEllipsisStyle,
    },
    {
      name: 'Date',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      selector: 'updatedAt',
      sortable: true,
    },
    {
      name: 'Template name',
      selector: 'template.name',
      cell: (row) => (row.template as any)?.name,
    },
    {
      name: 'Template version',
      selector: 'template.version',
      cell: (row) => (row.template as any)?.version,
    },
    {
      name: 'Template _id',
      selector: 'template._id',
      cell: (row) => (row.template as any)?._id,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => {
        const secondaryActionsList = [
          {
            iconComponent: <IconAdjustmentsAlt color={theme.colors[theme.primaryColor][6]} />,
            text: 'Change Transit Status',
            onClick: () => openChangeTransitStatusModal(row),
          },
          {
            iconComponent: <IconDownload color={theme.colors[theme.primaryColor][6]} />,
            text: 'Export Transit',
            onClick: () => exportTransitMutation.mutate(row._id),
            loading: exportTransitMutation.isPending,
          },
        ];

        return <TableActions actions={[]} secondaryActions={secondaryActionsList} showDebugMenu={false} transitView={transitView} />;
      },
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Transits</ContentLayout.Title>
        <ContentLayout.Filters>
          <Button leftSection={<IconUpload size={16} />} onClick={openImportTransitModal}>
            Import Transit
          </Button>
          <TransitType
            value={transitView}
            onChange={(item) => {
              loadTransits(item);
              setTransitView(item);
            }}
          />
          <SearchBox handleSearch={searchTransits} value={filters?.searchQuery} placeholder="Search by counter, name LRN or MRN" />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={transitsData ?? []}
          total={totalTransitCount}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
